import React from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownToLine, faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import { StatusPill } from 'components/EmailMarketing/UI/StatusPill/StatusPill';
import { DataExport } from 'model/dataExports';
import {
    DISPLAY_STATUS_PILL,
    buildFileName,
    formatDateRange,
    formatExpiresIn,
    formatRequestedAt,
    formatRowCount,
    getDisplayStatus,
    getResourceOption
} from '../dataExportUtils';
import styles from './DataExportCard.module.scss';

interface DataExportCardProps {
    dataExport: DataExport;
    locationsLabel: string;
    highlighted?: boolean;
    rerunDisabled?: boolean;
    onRerun: (dataExport: DataExport) => void;
}

const download = (dataExport: DataExport) => {
    if (!dataExport.downloadUrl) return;

    const link = document.createElement('a');
    link.href = dataExport.downloadUrl;
    link.download = buildFileName(dataExport);
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
};

export const DataExportCard: React.FC<DataExportCardProps> = ({
    dataExport,
    locationsLabel,
    highlighted,
    rerunDisabled,
    onRerun
}) => {
    const resource = getResourceOption(dataExport.resource);
    const status = getDisplayStatus(dataExport);
    const pill = DISPLAY_STATUS_PILL[status];

    const details = [
        locationsLabel,
        formatDateRange(dataExport.startDate, dataExport.endDate),
        status === 'ready' ? formatRowCount(dataExport.rowCount) : null
    ].filter(Boolean);

    const requested = [
        dataExport.requestedBy ? `Requested by ${dataExport.requestedBy}` : 'Requested',
        formatRequestedAt(dataExport.createdAt),
        status === 'ready' ? formatExpiresIn(dataExport.urlExpiresAt) : null
    ].filter(Boolean);

    const renderAction = () => {
        switch (status) {
            case 'ready':
                return (
                    <Button
                        variant="outlined"
                        leftIcon={faArrowDownToLine}
                        label="Download"
                        className={styles.actionButton}
                        onClick={() => download(dataExport)}
                    />
                );
            case 'failed':
                return (
                    <Button
                        variant="outlined"
                        leftIcon={faArrowRotateRight}
                        label="Try again"
                        disabled={rerunDisabled}
                        className={styles.actionButton}
                        onClick={() => onRerun(dataExport)}
                    />
                );
            case 'expired':
                return (
                    <Button
                        variant="outlined"
                        leftIcon={faArrowRotateRight}
                        label="Run again"
                        disabled={rerunDisabled}
                        className={styles.actionButton}
                        onClick={() => onRerun(dataExport)}
                    />
                );
            default:
                return <span className={styles.preparingHint}>Usually ready in a few minutes</span>;
        }
    };

    return (
        <li className={clsx(styles.root, { [styles.highlighted]: highlighted })}>
            <span className={clsx(styles.iconTile, { [styles.iconTileMuted]: status === 'expired' })}>
                <FontAwesomeIcon icon={resource.icon} />
            </span>

            <div className={styles.body}>
                <div className={styles.titleRow}>
                    <h3 className={styles.title}>{resource.label}</h3>
                    <StatusPill
                        label={pill.label}
                        tone={pill.tone}
                    />
                </div>
                <p className={styles.details}>{details.join(' · ')}</p>
                <p className={styles.requested}>{requested.join(' · ')}</p>
            </div>

            <div className={styles.action}>{renderAction()}</div>
        </li>
    );
};

export default DataExportCard;
